import React, { memo } from "react";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { cn } from "@/lib/utils";

export type BadgeVariant =
  | "filled-success"
  | "filled-warning"
  | "filled-error"
  | "accent-green"
  | "accent-orange"
  | "accent-blue"
  | "accent-purple"
  | "neutral"
  | "default";

export interface BadgeProps {
  label: TranslationKeys | string;
  variant?: BadgeVariant;
  /**
   * Controls the border-radius shape.
   * - `"default"` (default) — uses the per-variant radius (rounded / rounded-sm).
   * - `"rounded"` — forces `rounded-full` (pill shape) regardless of variant.
   */
  shape?: "default" | "rounded";
  testID?: string;
  className?: string;
  labelClassName?: string;
}

const BADGE_STYLES: Record<
  BadgeVariant,
  { bg: string; border?: string; text: string; rounded: string }
> = {
  "filled-success": {
    bg: "bg-success-light",
    border: "border-success-border",
    text: "text-success",
    rounded: "rounded",
  },
  "filled-warning": {
    bg: "bg-orange-light",
    border: "border-orange-border",
    text: "text-orange",
    rounded: "rounded",
  },
  "filled-error": {
    bg: "bg-error-light",
    border: "border-error-border",
    text: "text-error",
    rounded: "rounded",
  },
  "accent-green": { bg: "bg-green-50", text: "text-stroke-0", rounded: "rounded-sm" },
  "accent-orange": { bg: "bg-orange-50", text: "text-stroke-0", rounded: "rounded-sm" },
  "accent-blue": { bg: "bg-blue-50", text: "text-stroke-0", rounded: "rounded-sm" },
  "accent-purple": { bg: "bg-purple-50", text: "text-stroke-0", rounded: "rounded-sm" },
  neutral: { bg: "bg-stroke-50", text: "text-supporting-text-40", rounded: "rounded-sm" },
  default: { bg: "bg-stroke-20", text: "text-dark-text-50", rounded: "rounded-sm" },
};

export const Badge = memo(function Badge({
  label,
  variant = "default",
  shape = "default",
  testID,
  className = "",
  labelClassName,
}: BadgeProps) {
  const { t } = useI18n();
  const config = BADGE_STYLES[variant];
  const radiusClass = shape === "rounded" ? "rounded-full" : config.rounded;

  const isTranslationKey = typeof label === "string" && label.includes(".");
  const displayLabel = isTranslationKey ? t(label as TranslationKeys) : label;

  return (
    <View
      testID={testID}
      className={cn(
        "self-start px-3 py-1 items-center justify-center",
        radiusClass,
        config.bg,
        config.border ? `border ${config.border}` : "",
        className
      )}
      accessibilityRole="text"
    >
      <AppText weight="medium" className={cn("text-sm tracking-wide", config.text, labelClassName)}>
        {displayLabel}
      </AppText>
    </View>
  );
});
